// Localização: frontend/src/utils/formatters.js (versão 0.1.2)

/**
 * Converte uma string de data/hora do banco de dados (TIMESTAMP WITHOUT TIME ZONE)
 * para um objeto Date interpretando como hora local, não UTC.
 * 
 * O PostgreSQL retorna TIMESTAMP WITHOUT TIME ZONE como strings no formato:
 * - "2024-01-15 14:30:00" (formato SQL padrão)
 * - "2024-01-15T14:30:00.000Z" (formato ISO com Z, mas sem timezone real)
 * 
 * Esta função extrai os componentes da data e cria um Date usando o construtor
 * que aceita ano, mês, dia, hora, minuto, segundo - isso força interpretação local.
 * 
 * @param {string | Date} dateString - A string de data/hora do banco ou objeto Date.
 * @returns {Date} Objeto Date interpretado como hora local.
 */
export const parseLocalDateTime = (dateString) => {
    if (!dateString) return null;
    
    // Se já é um Date, retorna ele mesmo
    if (dateString instanceof Date) {
        return dateString;
    }
    
    try {
        const originalString = String(dateString).trim();
        
        // Primeiro, tentar extrair componentes diretamente da string (formato mais comum do PostgreSQL)
        // Formatos esperados:
        // - "2025-11-28 12:00:00" (formato PostgreSQL TIMESTAMP WITHOUT TIME ZONE)
        // - "2025-11-28T12:00:00" (formato ISO sem timezone)
        // - "2025-11-28T12:00:00.000Z" (formato ISO com Z - UTC)
        // - "2025-11-28T12:00:00+00:00" (formato ISO com offset)
        
        // Remover milissegundos se existirem
        let cleanString = originalString.replace(/\.\d{3,}/, '');
        
        // Detectar se tem timezone (Z ou offset)
        const hasZ = cleanString.endsWith('Z');
        const hasTimezoneOffset = /[+-]\d{2}:?\d{2}$/.test(cleanString);
        
        // Se tem timezone, remover para tratar como local
        if (hasZ) {
            cleanString = cleanString.replace(/Z$/, '');
        } else if (hasTimezoneOffset) {
            cleanString = cleanString.replace(/[+-]\d{2}:?\d{2}$/, '');
        }
        
        // Normalizar separador de data/hora
        cleanString = cleanString.replace(' ', 'T');
        
        // Extrair componentes usando regex
        // Formatos: YYYY-MM-DDTHH:mm:ss ou YYYY-MM-DDTHH:mm
        const dateTimeMatch = cleanString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
        
        if (dateTimeMatch) {
            // Extrair componentes: ano, mês (1-indexed), dia, hora, minuto, segundo (opcional)
            const year = parseInt(dateTimeMatch[1], 10);
            const month = parseInt(dateTimeMatch[2], 10) - 1; // JavaScript months are 0-indexed
            const day = parseInt(dateTimeMatch[3], 10);
            const hour = parseInt(dateTimeMatch[4], 10);
            const minute = parseInt(dateTimeMatch[5], 10);
            const second = dateTimeMatch[6] ? parseInt(dateTimeMatch[6], 10) : 0;
            
            // Validar componentes
            if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute) || isNaN(second)) {
                console.warn('[parseLocalDateTime] Componentes inválidos:', { year, month, day, hour, minute, second }, 'de:', dateString);
                return null;
            }
            
            // Criar Date usando construtor local (ano, mês, dia, hora, minuto, segundo)
            // Isso força a interpretação como hora local, não UTC
            const date = new Date(year, month, day, hour, minute, second);
            
            if (isNaN(date.getTime())) {
                console.warn('[parseLocalDateTime] Data inválida após criação:', dateString, 'Componentes:', { year, month: month + 1, day, hour, minute, second });
                return null;
            }
            
            // Verificar se a data criada corresponde aos componentes (proteção contra overflow)
            if (date.getFullYear() !== year || 
                date.getMonth() !== month || 
                date.getDate() !== day ||
                date.getHours() !== hour ||
                date.getMinutes() !== minute ||
                date.getSeconds() !== second) {
                console.warn('[parseLocalDateTime] Data criada não corresponde aos componentes:', {
                    esperado: { year, month: month + 1, day, hour, minute, second },
                    obtido: {
                        year: date.getFullYear(),
                        month: date.getMonth() + 1,
                        day: date.getDate(),
                        hour: date.getHours(),
                        minute: date.getMinutes(),
                        second: date.getSeconds()
                    },
                    original: dateString
                });
            }
            
            return date;
        }
        
        // Fallback: tentar criar Date diretamente (pode causar problemas de timezone)
        console.warn('[parseLocalDateTime] Usando fallback para:', dateString);
        const date = new Date(originalString);
        
        if (isNaN(date.getTime())) {
            console.warn('[parseLocalDateTime] Data inválida no fallback:', dateString);
            return null;
        }
        
        return date;
    } catch (error) {
        console.error('[parseLocalDateTime] Erro ao converter data:', error, dateString);
        return null;
    }
};

/**
 * Converte uma string datetime-local (formato "YYYY-MM-DDTHH:mm") para o formato PostgreSQL
 * ("YYYY-MM-DD HH:mm:ss") sem conversão de timezone.
 * 
 * Esta função é usada para enviar datas do frontend para o backend quando o banco usa
 * TIMESTAMP WITHOUT TIME ZONE. Preserva o valor exato do horário local.
 * 
 * @param {string} datetimeLocalString - String no formato datetime-local ("YYYY-MM-DDTHH:mm")
 * @returns {string} String no formato PostgreSQL ("YYYY-MM-DD HH:mm:ss") ou null se inválida
 * 
 * @example
 * formatDateTimeLocalToPostgres("2025-01-15T14:30") // "2025-01-15 14:30:00"
 */
export const formatDateTimeLocalToPostgres = (datetimeLocalString) => {
    if (!datetimeLocalString) return null;
    
    try {
        // O formato datetime-local é "YYYY-MM-DDTHH:mm"
        // Extrair componentes diretamente sem criar Date (evita conversão de timezone)
        const match = String(datetimeLocalString).trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
        
        if (!match) {
            console.warn('[formatDateTimeLocalToPostgres] Formato inválido:', datetimeLocalString);
            return null;
        }
        
        const [, year, month, day, hour, minute] = match;
        
        // Validar componentes
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);
        
        if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum) || isNaN(hourNum) || isNaN(minuteNum)) {
            console.warn('[formatDateTimeLocalToPostgres] Componentes inválidos:', { year, month, day, hour, minute });
            return null;
        }
        
        // Validar ranges
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31 || 
            hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
            console.warn('[formatDateTimeLocalToPostgres] Valores fora do range válido');
            return null;
        }
        
        // Retornar no formato PostgreSQL: "YYYY-MM-DD HH:mm:ss"
        return `${year}-${month}-${day} ${hour}:${minute}:00`;
    } catch (error) {
        console.error('[formatDateTimeLocalToPostgres] Erro ao converter:', error, datetimeLocalString);
        return null;
    }
};

/**
 * Converte uma string de data/hora (ISO) ou um objeto Date para o formato DD/MM/AAAA.
 * @param {string | Date} dateString - A string de data/hora ou objeto Date.
 * @returns {string} A data formatada (DD/MM/AAAA) ou 'N/A' se for inválida.
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        
        // Verifica se a data é válida (getTime() retorna NaN para datas inválidas)
        if (isNaN(date.getTime())) {
            return 'Data Inválida';
        }
        
        // Formato brasileiro (DD/MM/AAAA)
        return date.toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    } catch (error) {
        console.error("Erro ao formatar data:", error);
        return 'N/A';
    }
};

/**
 * Converte uma string de data/hora (ISO) ou um objeto Date para o formato DD/MM/AAAA HH:mm.
 * @param {string | Date} dateString - A string de data/hora ou objeto Date.
 * @returns {string} A data e hora formatada (DD/MM/AAAA HH:mm) ou 'N/A' se for inválida.
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return 'Data Inválida';
        }

        return date.toLocaleDateString('pt-BR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false // Formato 24 horas
        });
    } catch (error) {
        console.error("Erro ao formatar data e hora:", error);
        return 'N/A';
    }
};

/**
 * Formata um valor numérico como moeda brasileira (Real).
 * @param {number} value - O valor a ser formatado.
 * @returns {string} O valor formatado como R$ X.XXX,XX.
 */
export const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
    }
    return Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
};

/**
 * Formata um nome completo para 'Primeiro Nome Sobrenome'.
 * Ex: 'João da Silva Sauro' -> 'João S. Sauro'
 * @param {string} nomeCompleto - Nome completo.
 * @returns {string} Nome formatado.
 */
export const formatShortName = (nomeCompleto) => {
    if (!nomeCompleto) return 'N/A';
    
    const parts = nomeCompleto.trim().split(' ');
    if (parts.length === 1) return parts[0];

    const firstName = parts[0];
    
    // Simplificado para apenas Primeiro Nome + Último Nome
    return `${firstName} ${parts[parts.length - 1]}`;
};

/**
 * Calcula a idade de uma pessoa a partir da sua data de nascimento.
 * @param {string | Date} birthDate - A data de nascimento (ISO string ou Date object).
 * @returns {number | null} A idade calculada em anos, ou null se a data for inválida.
 */
export const calculateAge = (birthDate) => {
    if (!birthDate) return null;

    try {
        const today = new Date();
        const birth = new Date(birthDate);

        if (isNaN(birth.getTime())) {
            return null;
        }

        let age = today.getFullYear() - birth.getFullYear();
        const monthDifference = today.getMonth() - birth.getMonth();
        
        // Se o mês atual for menor que o mês de nascimento, ou
        // se o mês for o mesmo, mas o dia atual for menor que o dia de nascimento
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
            age--;
        }

        return age;

    } catch (error) {
        console.error("Erro ao calcular idade:", error);
        return null;
    }
};